import { useRef } from "react";
import { toast } from "sonner";
import { Copy, CreditCard, Landmark, Upload, FileCheck2, X } from "lucide-react";

const RECEIPT_ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.heic,.gif,application/pdf,image/*";

export function PayMethodPicker({ cardEnabled, method, setMethod }) {
  const opts = [...(cardEnabled ? [["card", "Pay by card", CreditCard]] : []), ["etransfer", "Interac e-Transfer", Landmark]];
  if (opts.length < 2) return null;
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="pub-pay-method">
      {opts.map(([k, label, Icon]) => (
        <button key={k} type="button" data-testid={`pub-pay-${k}`} onClick={() => setMethod(k)}
          className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-semibold transition-colors ${method === k ? "bg-secondary text-white border-secondary" : "bg-white border-slate-200 text-slate-600 hover:border-secondary/40"}`}>
          <Icon className="h-4 w-4" /> {label}
        </button>
      ))}
    </div>
  );
}

export function EtransferBox({ email, amountLabel, receipt, setReceipt }) {
  const ref = useRef(null);
  const copy = () => navigator.clipboard?.writeText(email).then(() => toast.success("Email copied"), () => toast.error("Could not copy"));
  const pick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(pdf|jpe?g|png|webp|heic|gif)$/i.test(f.name)) { toast.error("Please choose a PDF or image (JPG, PNG, HEIC)"); return; }
    if (f.size > 10 * 1024 * 1024) { toast.error("Receipt is too large (max 10 MB)"); return; }
    setReceipt(f);
  };
  return (
    <div className="rounded-xl bg-amber-50/60 border border-amber-200 p-3 space-y-2.5 text-sm" data-testid="pub-etransfer-box">
      <ol className="list-decimal pl-5 space-y-1 text-slate-700">
        <li>Send <b data-testid="pub-etransfer-amount">{amountLabel}</b> by Interac e-Transfer to:</li>
      </ol>
      <div className="flex items-center gap-2 bg-white rounded-lg border border-amber-200 px-3 py-2">
        <span className="font-semibold text-slate-900 break-all flex-1" data-testid="pub-etransfer-email">{email}</span>
        <button type="button" onClick={copy} data-testid="pub-etransfer-copy" className="text-xs font-semibold text-primary inline-flex items-center gap-1 shrink-0"><Copy className="h-3.5 w-3.5" /> Copy</button>
      </div>
      <ol start={2} className="list-decimal pl-5 text-slate-700"><li>Upload the receipt or a screenshot of the completed transfer below.</li></ol>
      <input ref={ref} type="file" accept={RECEIPT_ACCEPT} className="hidden" onChange={pick} data-testid="pub-receipt-input" />
      {receipt ? (
        <div className="flex items-center gap-2 bg-white rounded-lg border border-emerald-200 px-3 py-2" data-testid="pub-receipt-chosen">
          <FileCheck2 className="h-4 w-4 text-emerald-600 shrink-0" /><span className="truncate flex-1 text-slate-700">{receipt.name}</span>
          <button type="button" onClick={() => setReceipt(null)} data-testid="pub-receipt-remove" className="text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>
        </div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} data-testid="pub-receipt-upload"
          className="w-full border-2 border-dashed border-amber-300 rounded-lg py-3 text-amber-800 font-semibold inline-flex items-center justify-center gap-2 hover:bg-amber-50">
          <Upload className="h-4 w-4" /> Upload receipt or screenshot (PDF / image)
        </button>
      )}
      <p className="text-[11px] text-slate-500">Your appointment is booked as soon as the receipt is uploaded. Our team will check the transfer has arrived.</p>
    </div>
  );
}
